import React, { useCallback, useContext, useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import Button from 'kingpin/atoms/Button';
import aguid from 'lib/aguid';

import ConfigureDashboardGadget from './ConfigureDashboardGadget';
import Loading from '../Loading';
import WidgetGalleryContext from '../../contexts/WidgetGalleryContext';
import withoutNulls from '../../api/expression/withoutNulls';
import ComparisonContext from '../../contexts/ComparisonContext';
import DashboardContext from '../../contexts/DashboardContext';
import useDashboardGadgetDataTypes from '../../hooks/useDashboardGadgetDataTypes';
import BonusPeriodsContext from '../../contexts/BonusPeriodsContext';
import migrateRelativeDateRange from '../../migrateRelativeDateRange';

import { GadgetFormContainer } from '../GadgetForm';
import Row from '../Common/Row';
import CloseModal from '../CloseModal';
import CurrentUserContext from '../../contexts/CurrentUserContext';
import withDateFilter from '../../hocs/withDateFIlter';
import DateInputContext from '../../contexts/DateInputContext';
import { LAST_MONTH } from '../DateInput/constants';
import isRemindersGadget from '../../types/visTypeCheckers/isRemindersGadget';
import FilterPlatesProvider from '../../contextProviders/FilterPlatesProvider';
import isSingleMetricDateMatrix from '../../types/visTypeCheckers/isSingleMetricDateMatrix';
import isPaceMatrix from '../../types/visTypeCheckers/isPaceMatrix';
import { SortToggleProvider } from '../V5GadgetForm/SortToggleContext';

const sanitizeGadget = (gadget: DashboardGadget) => withoutNulls(gadget);

export const parseSavedDateRange = (
  dateRange: DateRangeInput | DateRangeInput[] | undefined,
) => {
  if (dateRange === undefined) {
    return undefined;
  }

  if (Array.isArray(dateRange)) {
    if (dateRange.length > 0) {
      return dateRange[0];
    }
    return undefined;
  }

  return dateRange;
};

const ConfigureDashboardGadgetContainer = ({
  dashboardGadgetChart,
  onEditSave,
  editingGadget,
  close,
}: {
  dashboardGadgetChart?: VisualisationDefinition;
  onEditSave?: (g: DashboardGadget) => void;
  editingGadget?: DashboardGadget;
  close: () => void;
}) => {
  const currentUser = useContext(CurrentUserContext);
  const { resetDashboardGadgetEditorRef, onConfigureSave } =
    useContext(WidgetGalleryContext);
  const [chartDef, setChartDef] = useState<VisualisationDefinition | undefined>(
    () =>
      dashboardGadgetChart ||
      (editingGadget ? editingGadget.chartDef : undefined),
  );

  const { setIsEditingCard, dashboard } = useContext(DashboardContext);
  const {
    dateField,
    dateRange,
    relativeDateRange,
    advancedRelativeDateRange,
    setDateField,
    setDateRange,
    setRelativeDateRange,
    setAdvancedRelativeDateRange,
    setDataTypes,
  } = useContext(DateInputContext);
  const { selectedBonusPeriod } = useContext(BonusPeriodsContext);
  const usedDataTypes = useDashboardGadgetDataTypes(editingGadget);
  const [drillDowns, setDrillDowns] = useState<FilterPlateType[]>(
    editingGadget ? editingGadget.drillDowns : [],
  );
  const [weekStartsOnOverride, setWeekStartsOnOverride] = useState<
    WeekStartsOn | undefined
  >(editingGadget ? editingGadget.weekStartsOnOverride : undefined);
  const [comparison, setComparison] = useState<
    PersistedComparisonType | undefined
  >(editingGadget ? editingGadget.comparison : undefined);
  const [scope, setScope] = useState<FilterPlateType[]>(
    editingGadget ? editingGadget.scope : [],
  );
  const [isDefaultFiltersDisabled, setIsDefaultFiltersDisabled] =
    useState<boolean>(
      editingGadget ? !!editingGadget.isDefaultFiltersDisabled : false,
    );
  const [reportDrillDownId, setReportDrillDownId] = useState<
    string | undefined
  >(editingGadget ? editingGadget.reportDrillDownId : undefined);
  const [boardDrillDownId, setBoardDrillDownId] = useState<string | undefined>(
    editingGadget ? editingGadget.boardDrillDownId : undefined,
  );
  const [isBonusPeriodMode, setIsBonusPeriodMode] = useState<boolean>(
    editingGadget ? !!editingGadget.isBonusPeriodMode : false,
  );
  const [isDashboardFiltersIgnored, setIsDashboardFiltersIgnored] =
    useState<boolean>(
      editingGadget ? !!editingGadget.isDashboardFiltersIgnored : false,
    );
  const [isEntityFilterEnabled, setIsEntityFilterEnabled] = useState<boolean>(
    () => {
      if (editingGadget) {
        return !!editingGadget.isEntityFilterEnabled;
      }
      return true;
    },
  );

  useEffect(() => {
    setDataTypes(usedDataTypes);
  }, [setDataTypes, usedDataTypes]);

  useEffect(() => {
    // Reset date range values to default after toggling bonus period mode
    // Prevents bug where date range values are broken after toggle
    if (isBonusPeriodMode) {
      return;
    }

    setDateRange(
      editingGadget ? parseSavedDateRange(editingGadget.dateRange) : undefined,
    );
    setRelativeDateRange(
      editingGadget
        ? editingGadget.relativeDateRange
          ? migrateRelativeDateRange(editingGadget.relativeDateRange)
          : undefined
        : LAST_MONTH,
    );
    setAdvancedRelativeDateRange(
      editingGadget ? editingGadget.advancedRelativeDateRange : undefined,
    );
    setDateField(editingGadget ? editingGadget.dateField : 'date');
  }, [
    editingGadget,
    isBonusPeriodMode,
    setAdvancedRelativeDateRange,
    setDateField,
    setDateRange,
    setRelativeDateRange,
  ]);

  const resetDashboardGadgetEditor = useCallback(() => {
    setChartDef(undefined);
    setDateRange(undefined);
    setRelativeDateRange(undefined);
    setAdvancedRelativeDateRange(undefined);
    setDrillDowns([]);
    setDateField('date');
    setComparison(undefined);
    setScope([]);
    setReportDrillDownId(undefined);
    setBoardDrillDownId(undefined);
    setIsBonusPeriodMode(false);
    setWeekStartsOnOverride(undefined);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (resetDashboardGadgetEditorRef) {
      resetDashboardGadgetEditorRef.current = resetDashboardGadgetEditor;
    }
  }, [resetDashboardGadgetEditor, resetDashboardGadgetEditorRef]);

  const dataTypes = useDashboardGadgetDataTypes(editingGadget, chartDef);

  useEffect(() => {
    setIsEditingCard(true);
    return () => {
      setIsEditingCard(false);
    };
  }, [setIsEditingCard]);

  const onSave = useCallback(() => {
    if (!chartDef) {
      return;
    }

    const gadget: DashboardGadget = {
      id: editingGadget ? editingGadget.id : aguid(),
      drillDowns,
      dateRange,
      dateField,
      relativeDateRange,
      advancedRelativeDateRange,
      comparison,
      scope,
      chartDef,
      boardDrillDownId: boardDrillDownId,
      reportDrillDownId,
      isBonusPeriodMode,
      createdBy: editingGadget ? editingGadget.createdBy : currentUser.id,
      createdOn: editingGadget
        ? editingGadget.createdOn
        : DateTime.utc().toISO(),
      updatedBy: currentUser.id,
      updatedOn: DateTime.utc().toISO(),
      weekStartsOnOverride,
      isEntityFilterEnabled,
      isDefaultFiltersDisabled,
      isDashboardFiltersIgnored: isDashboardFiltersIgnored || undefined,
      // A Studio block's own settings. They stay with the gadget: they only
      // count while they still rebuild its chart (see readStudioBlock), so
      // one this changes simply reads as a classic gadget.
      ...(editingGadget && editingGadget.studio
        ? { studio: editingGadget.studio }
        : {}),
      // Where it was made, as it stays however it's edited
      ...(editingGadget && editingGadget.createdIn
        ? { createdIn: editingGadget.createdIn }
        : {}),
    };
    if (onEditSave) {
      onEditSave(sanitizeGadget(gadget));
    } else {
      onConfigureSave(sanitizeGadget(gadget));
    }
  }, [
    advancedRelativeDateRange,
    boardDrillDownId,
    chartDef,
    comparison,
    currentUser.id,
    dateField,
    dateRange,
    drillDowns,
    editingGadget,
    isBonusPeriodMode,
    isDashboardFiltersIgnored,
    isDefaultFiltersDisabled,
    isEntityFilterEnabled,
    onConfigureSave,
    onEditSave,
    relativeDateRange,
    reportDrillDownId,
    scope,
    weekStartsOnOverride,
  ]);

  const onIsBonusPeriodModeChanged = useCallback(
    (newStatus: boolean) => {
      setIsBonusPeriodMode(newStatus);
      if (newStatus) {
        setDateRange(undefined);
        setRelativeDateRange(undefined);
      }
    },
    [setDateRange, setRelativeDateRange],
  );

  useEffect(() => {
    if (!chartDef || !!comparison) {
      return;
    }
    if (isSingleMetricDateMatrix(chartDef)) {
      setComparison({
        compareType: 'compareTo',
        relativeDateOption: 'last 7 days',
      });
      return;
    }

    if (isPaceMatrix(chartDef)) {
      setComparison({
        compareType: 'compareTo',
        relativeDateOption: 'last 4 weeks',
      });
    }
  }, [chartDef, comparison]);

  const isValid = isRemindersGadget(chartDef)
    ? chartDef.events.length > 0 && chartDef.name !== ''
    : true;

  if (!dashboard) {
    return <Loading />;
  }

  if (editingGadget && !chartDef) {
    return <Loading />;
  }

  return (
    <ComparisonContext.Provider
      value={{
        currentComparison: comparison,
        setCurrentComparison: setComparison,
      }}
    >
      <FilterPlatesProvider
        drillDowns={drillDowns}
        setDrillDowns={setDrillDowns}
        scope={scope}
        setScope={setScope}
        dataTypes={dataTypes}
        isDefaultFiltersDisabled={isDefaultFiltersDisabled}
        setIsDefaultFiltersDisabled={setIsDefaultFiltersDisabled}
      >
        <CloseModal close={close} />
        <SortToggleProvider>
          <Row
            style={{
              height: '80vh',
              position: 'relative',
            }}
          >
            <div
              style={{
                width: '25%',
                overflowY: 'scroll',
                borderRight: '1px solid var(--color-bg-input)',
              }}
            >
              <GadgetFormContainer
                selected={chartDef}
                onChange={setChartDef}
                isEditing={!!editingGadget}
              />
            </div>
            <div style={{ width: '75%' }}>
              <div
                style={{
                  overflowY: 'scroll',
                  height: 'calc(80vh - 56px)',
                }}
              >
                <ConfigureDashboardGadget
                  chartDef={chartDef}
                  reportDrillDownId={reportDrillDownId}
                  setReportDrillDownId={setReportDrillDownId}
                  boardDrillDownId={boardDrillDownId}
                  setBoardDrillDownId={setBoardDrillDownId}
                  isBonusPeriodMode={isBonusPeriodMode}
                  onIsBonusPeriodModeChanged={onIsBonusPeriodModeChanged}
                  selectedBonusPeriod={selectedBonusPeriod}
                  isValid={isValid}
                  weekStartsOnOverride={weekStartsOnOverride}
                  setWeekStartsOnOverride={setWeekStartsOnOverride}
                  isEntityFilterEnabled={isEntityFilterEnabled}
                  setIsEntityFilterEnabled={setIsEntityFilterEnabled}
                  isDashboardFiltersIgnored={isDashboardFiltersIgnored}
                  setIsDashboardFiltersIgnored={setIsDashboardFiltersIgnored}
                />
              </div>
              <Row
                spaceBetween
                centerAlign
                style={{
                  height: 56,
                  paddingRight: 22,
                  paddingLeft: 22,
                  borderTop: '1px solid var(--color-bg-input)',
                  alignItems: 'center',
                }}
              >
                <Button
                  onClick={close}
                  type="Secondary"
                  size="Small"
                  label="Close"
                />
                <div style={{ height: 30 }}>
                  <Button
                    onClick={onSave}
                    isDisabled={!isValid}
                    label="Save"
                    type="Primary"
                    size="Small"
                  />
                </div>
              </Row>
            </div>
          </Row>
        </SortToggleProvider>
      </FilterPlatesProvider>
    </ComparisonContext.Provider>
  );
};

export default withDateFilter(ConfigureDashboardGadgetContainer, {});
